import type { FC } from "react";

import type { ExecutionFileChange, ExecutionVisualChange } from "../../types";

import { EyebrowLabel } from "../common/EyebrowLabel";
import { DocumentIcon, EyeIcon } from "../common/icons";
import { ListItemCard } from "../common/ListItemCard";
import { StatusBadge } from "../common/StatusBadge";
import { SummaryFormatter } from "../common/SummaryFormatter";

interface VisualChangeCardProps {
  file: ExecutionFileChange;
  change: ExecutionVisualChange;
}

export const VisualChangeCard: FC<VisualChangeCardProps> = ({
  file,
  change,
}) => {
  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <EyeIcon
        size={14}
        className="flex-shrink-0 text-emerald-600 dark:text-emerald-300"
      />
      <span
        className="break-all font-mono text-xs text-gray-700 dark:text-gray-200"
        title={file.path}
      >
        {file.path}
      </span>
      <StatusBadge status={file.status} size="sm" withIcon={false} />
    </div>
  );

  const actions = (
    <div className="flex items-center gap-3 font-mono text-xs text-gray-500 dark:text-gray-400">
      {file.added_lines != null && (
        <span className="text-emerald-600 dark:text-emerald-400">
          +{file.added_lines}
        </span>
      )}
      {file.removed_lines != null && (
        <span className="text-rose-600 dark:text-rose-400">
          -{file.removed_lines}
        </span>
      )}
    </div>
  );

  return (
    <ListItemCard
      header={header}
      actions={actions}
      className="border-gray-200 bg-white dark:border-white/10 dark:bg-white/5"
    >
      {/* Summary */}
      <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <EyebrowLabel>Summary</EyebrowLabel>
        <SummaryFormatter text={change.summary} />
      </div>

      {/* Assets */}
      {change.assets && change.assets.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {change.assets.map((asset, index) => (
            <span
              key={`${asset}-${index}`}
              className="inline-flex items-center gap-1 border border-slate-200 bg-slate-100 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-200"
            >
              <DocumentIcon size={12} className="text-gray-400" />
              {asset}
            </span>
          ))}
        </div>
      )}

      {/* Notes */}
      {change.notes && (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          <EyebrowLabel>Notes</EyebrowLabel>
          {change.notes}
        </p>
      )}
    </ListItemCard>
  );
};
